import { addMonths, format, differenceInCalendarDays } from "date-fns";

// ── The plan catalogue: the single source of truth for pricing ───────────────
//
// Only `months` and `priceUsd` are stated. Every per-month rate, saving and
// percentage on the screen is DERIVED from those two, so a price edit here
// cannot leave a stale "save $101" behind in a component. That drift is not
// hypothetical: production quote rows still carry `1 Year - $199 (save $50)`
// from when the figure was written by hand.
//
// NOT related to this file: `Products.price` in the database ($299 / $399, held
// in integer cents). That column feeds the dormant Paymob order path and is a
// different SKU set entirely. It is deliberately not the source of truth here,
// and nothing on this screen should read it.
//
// Badges live in config so one can be moved between plans without touching a
// component. Supported values: 'most_chosen', 'best_value', null.

export const PLANS = [
  {
    id: "monthly",
    label: "Monthly",
    // Wording for the renewal modal ("Extend by · 3 months").
    extendLabel: "1 month",
    // Term wording inside the string persisted on the quote row — see
    // toPlanPayload(). Kept separate from `label` so display copy can be
    // reworded without changing what sales reads in the admin listing.
    payloadLabel: "1 Month",
    months: 1,
    priceUsd: 25,
    badge: null,
  },
  {
    id: "quarterly",
    label: "3 months",
    extendLabel: "3 months",
    payloadLabel: "3 Month",
    months: 3,
    priceUsd: 60,
    badge: "most_chosen",
  },
  {
    id: "annual",
    label: "1 year",
    extendLabel: "1 year",
    payloadLabel: "1 Year",
    months: 12,
    priceUsd: 199,
    badge: "best_value",
  },
  {
    id: "triennial",
    label: "3 years",
    extendLabel: "3 years",
    payloadLabel: "3 Years",
    months: 36,
    priceUsd: 499,
    badge: null,
  },
];

// Custom pricing is NOT a member of PLANS: it has no price, so every derived
// value here (per-month, saving, percent) would be meaningless or NaN for it,
// and the derived-table test would have nothing to assert. It is a separate
// choice that happens to sit in the same row of cards.
//
// The payload is the bare string "Custom", which is what the old <select>
// submitted and what sales already recognises in the admin Quotes listing.
export const CUSTOM_PLAN = {
  id: "custom",
  label: "Something else",
  payload: "Custom",
};

export const isCustomPlan = (value) => value === CUSTOM_PLAN.payload;

export const BADGE_LABELS = {
  most_chosen: "Most chosen",
  best_value: "Best value",
};

// The yardstick every saving is measured against. Read off the monthly plan
// rather than restated, so there is exactly one place a rate change lands.
export const MONTHLY_RATE = PLANS.find((p) => p.id === "monthly").priceUsd;

// ── Derived values — never stored ────────────────────────────────────────────

export const pricePerMonth = (plan) => plan.priceUsd / plan.months;

/** What paying monthly for the same span would have cost. */
export const monthlyEquivalent = (plan) => MONTHLY_RATE * plan.months;

/** Whole dollars saved against paying monthly. 0 for the monthly plan itself. */
export const savingVsMonthly = (plan) => monthlyEquivalent(plan) - plan.priceUsd;

/** Fraction saved, 0–1. Callers format it. */
export const percentSaved = (plan) => savingVsMonthly(plan) / monthlyEquivalent(plan);

/** What a year on the monthly plan costs — the anchor the monthly card shows. */
export const annualCostAtMonthlyRate = () => MONTHLY_RATE * 12;

// ── Lookups ──────────────────────────────────────────────────────────────────

export const planById = (id) => PLANS.find((p) => p.id === id) || null;
export const longestPlan = () => PLANS.reduce((a, b) => (b.months > a.months ? b : a), PLANS[0]);

// Opens pre-selected so the submit button always carries an amount; a
// "Send my invoice" with no figure on it is the ambiguity that button exists to
// remove. The annual plan is the one the screen is built to promote.
export const DEFAULT_PLAN = PLANS.find((p) => p.badge === "best_value") || PLANS[0];

// ── The persisted plan string ────────────────────────────────────────────────
//
// `RequestQuote.plan` is a varchar written by the existing endpoints, which are
// out of scope here, so the form still submits a human-readable string rather
// than an id. Deriving it means the saving quoted to sales can never disagree
// with the saving shown on the card. The output reproduces the historical
// wording exactly — plans.test.js pins it so a refactor cannot quietly reword
// what lands in the admin listing.
export function toPlanPayload(plan) {
  if (!plan) return "";
  const base = `${plan.payloadLabel} - ${money(plan.priceUsd)}`;
  const saving = savingVsMonthly(plan);
  if (saving <= 0) return base;
  return `${base} (save ${money(saving)} - ${formatPercent(percentSaved(plan))})`;
}

/**
 * Resolve a stored plan string back to its plan.
 *
 * Only for the live form, where the string was produced by toPlanPayload() a
 * moment earlier. HISTORICAL quote rows must render their stored string as-is —
 * including the incorrect `save $50` ones — never be re-derived through here.
 */
export const planByPayload = (value) => PLANS.find((p) => toPlanPayload(p) === value) || null;

// ── Formatting ───────────────────────────────────────────────────────────────

/** "$25" — drops a trailing .00, which is noise on a round total. */
export function money(amount) {
  const rounded = Math.round(amount * 100) / 100;
  return `$${rounded.toFixed(Number.isInteger(rounded) ? 0 : 2)}`;
}

/** "$25.00" — always two places, so a row of per-month rates aligns. */
export function moneyExact(amount) {
  return `$${(Math.round(amount * 100) / 100).toFixed(2)}`;
}

export const formatPercent = (fraction) => `${Math.round(fraction * 100)}%`;

// ── Renewal dates ────────────────────────────────────────────────────────────

// Extensions add time to the CURRENT EXPIRY, never to today — that is the whole
// point of renewing early, and the modal says so out loud. date-fns clamps the
// month-end case (31 Jan + 1 month = 28 Feb, not 3 Mar).
export function extendedExpiry(currentExpiry, plan) {
  if (!currentExpiry || !plan) return null;
  const from = new Date(currentExpiry);
  if (Number.isNaN(from.getTime())) return null;
  return addMonths(from, plan.months);
}

export const formatDate = (d) => (d ? format(new Date(d), "d MMM yyyy") : "");
export const formatShortDate = (d) => (d ? format(new Date(d), "d MMM") : "");

/**
 * How the expiry banner should read. Calendar days, not elapsed 24h periods —
 * "expires in 1 day" has to mean "tomorrow", which a millisecond diff gets
 * wrong either side of midnight.
 */
export function expiryStatus(expiresAt, now = new Date()) {
  if (!expiresAt) return null;
  const exp = new Date(expiresAt);
  if (Number.isNaN(exp.getTime())) return null;

  const days = differenceInCalendarDays(exp, now);
  if (days < 0) return { tone: "expired", days: Math.abs(days), date: exp };
  if (days <= 30) return { tone: "soon", days, date: exp };
  return { tone: "ok", days, date: exp };
}
